import { z } from "zod";
import {
  DEVICE_MISSING_MEDIA_MAX,
  DEVICE_NAME_MAX,
  LINK_CODE_LENGTH,
  MAX_SLOTS,
} from "../constants";
import { sha256Schema } from "./media";
import { HOTKEY_MAX_LEN } from "./slot";

/** Uppercases and strips spaces and hyphens, so `kq7-m4pr` and `KQ7M4PR` are the same code. */
export function normalizeLinkCode(input: string): string {
  return input.toUpperCase().replace(/[\s-]/g, "");
}

/** Displays a normalized code as `XXX-XXXX`. */
export function formatLinkCode(code: string): string {
  const half = Math.floor(LINK_CODE_LENGTH / 2);
  return `${code.slice(0, half)}-${code.slice(half)}`;
}

/** Sent by the desktop app to redeem a link code. */
export const deviceLinkSchema = z.strictObject({
  code: z.string().max(32),
  deviceName: z.string().trim().min(1).max(DEVICE_NAME_MAX),
});

/** Returned once when a code is redeemed. The device key is shown only here. */
export const deviceLinkedSchema = z.object({
  deviceId: z.string(),
  deviceKey: z.string(),
  workspace: z.object({ id: z.string(), name: z.string() }),
});

/** What the device sees about itself when it checks its key. */
export const deviceMeSchema = z.object({
  deviceId: z.string(),
  deviceName: z.string(),
  workspace: z.object({ id: z.string(), name: z.string() }),
});

/** A linked device as listed on the web. Never includes the key or its hash. */
export const deviceViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  linked_by_email: z.string(),
  created_at: z.string(),
  last_seen_at: z.string().nullable(),
  revoked_at: z.string().nullable(),
});

/** A freshly generated link code, shown to the operator once. */
export const linkCodeCreatedSchema = z.object({
  code: z.string(),
  expires_at: z.string(),
});

export type DeviceLink = z.infer<typeof deviceLinkSchema>;
export type DeviceLinked = z.infer<typeof deviceLinkedSchema>;
export type DeviceMe = z.infer<typeof deviceMeSchema>;
export type DeviceView = z.infer<typeof deviceViewSchema>;
export type LinkCodeCreated = z.infer<typeof linkCodeCreatedSchema>;

/** A live configuration of the device's workspace, listed for the desktop's update check. */
export const deviceConfigurationSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  locked: z.boolean(),
  etag: z.string(),
});

export type DeviceConfigurationSummary = z.infer<typeof deviceConfigurationSummarySchema>;

/** Sent by the desktop to ask which of its media files the workspace does not hold yet. */
export const deviceMissingMediaSchema = z.object({
  sha256s: z.array(sha256Schema).min(1).max(DEVICE_MISSING_MEDIA_MAX),
});

/** The sha256 values from the request that the workspace holds no media row for. */
export const deviceMissingMediaResultSchema = z.object({ missing: z.array(sha256Schema) });

/** Returned by a device media upload. `created` is false for a dedupe of existing bytes. */
export const deviceMediaUploadedSchema = z.object({
  id: z.string(),
  sha256: sha256Schema,
  created: z.boolean(),
});

export type DeviceMissingMedia = z.infer<typeof deviceMissingMediaSchema>;
export type DeviceMissingMediaResult = z.infer<typeof deviceMissingMediaResultSchema>;
export type DeviceMediaUploaded = z.infer<typeof deviceMediaUploadedSchema>;

/** Song part of a desktop cell: `sha256: null` removes the song; `base` is the online sha it was edited from. */
const deviceUploadSongSchema = z.strictObject({
  sha256: sha256Schema.nullable(),
  label: z.string().max(120).nullable(),
  base: sha256Schema.nullable(),
});

const deviceUploadPhotoSchema = z.strictObject({
  sha256: sha256Schema.nullable(),
  base: sha256Schema.nullable(),
});

const deviceUploadRegionSchema = z.strictObject({
  startMs: z.number().int().min(0),
  lengthS: z.number().min(3).max(30),
  base: z.strictObject({ startMs: z.number().int().min(0), lengthS: z.number() }).nullable(),
});

/** One desktop cell. Player fields are present only when they changed locally since their base. */
const deviceUploadCellSchema = z.strictObject({
  clientRef: z.uuid(),
  personId: z.uuid().nullable(),
  name: z.string().trim().min(1).max(80),
  position: z.number().int().min(0),
  active: z.boolean(),
  hotkey: z.string().max(HOTKEY_MAX_LEN).nullable(),
  song: deviceUploadSongSchema.optional(),
  photo: deviceUploadPhotoSchema.optional(),
  region: deviceUploadRegionSchema.optional(),
});

/** A whole configuration from the desktop. Idempotent: the same document can be sent again. */
export const deviceUploadSchema = z.strictObject({
  clientRef: z.uuid(),
  configurationId: z.uuid().nullable(),
  name: z.string().trim().min(1).max(100),
  cells: z.array(deviceUploadCellSchema).max(MAX_SLOTS),
  deletedPersonIds: z.array(z.uuid()).max(MAX_SLOTS),
});

export const DEVICE_UPLOAD_SKIP_FIELDS = ["song", "photo", "region", "hotkey", "cell"] as const;

/** Per-cell outcome: what was written, and what was skipped with a reason. */
export const deviceUploadResultSchema = z.object({
  configurationId: z.uuid(),
  version: z.number().int(),
  etag: z.string(),
  cells: z.array(
    z.object({
      clientRef: z.uuid(),
      personId: z.uuid().nullable(),
      applied: z.array(z.string()),
      skipped: z.array(z.object({ field: z.enum(DEVICE_UPLOAD_SKIP_FIELDS), reason: z.string() })),
    }),
  ),
});

export type DeviceUpload = z.infer<typeof deviceUploadSchema>;
export type DeviceUploadResult = z.infer<typeof deviceUploadResultSchema>;
export type DeviceUploadCellOutcome = DeviceUploadResult["cells"][number];
export type DeviceUploadSkip = DeviceUploadCellOutcome["skipped"][number];
